import React, { useState } from 'react';
import { UserPlus, Loader2, Check, Clock, Mic2, LogOut, Sparkles } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { useToast } from '../hooks/use-toast';
import { requestCohost, cancelCohostRequest } from '../api/lives';
import liveDiag from '../lib/liveDiag'; // [LIVE-DIAG] temporary

/**
 * CohostRequestButton — "Partecipa alla live" (Phase 76 rename + stage flow).
 *
 * Rendered below the TipJar on LiveDetailPage for VIEWERS only (not the host).
 * States (live.my_cohost_status):
 *   - default:    "Partecipa alla live" request button + optional message
 *   - 'requested': waiting pill + cancel option
 *   - 'pending':   the HOST invited the viewer → InviteBanner handles it
 *   - 'accepted':  approved — the big "Sali sul palco" button lives ON the video
 *   - 'on_stage':  participating + "Lascia la partecipazione" button
 *   - 'declined':  rejected pill
 *
 * The leave button dispatches `nymia:leave-stage`, handled by LiveKitViewer:
 * ONE single leave flow (stop streams → remove guest → plain viewer).
 */
const CohostRequestButton = ({ live, onChanged }) => {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');

  if (!live) return null;
  if (live.is_owner) return null;
  if (live.status !== 'live') return null;

  const status = live.my_cohost_status;

  // The host invited me — let the existing invite banner handle it.
  if (status === 'pending') return null;

  const sendRequest = async () => {
    setBusy(true);
    try {
      await requestCohost(live.id, message.trim());
      liveDiag('participation_request_sent', { live_id: live.id }); // [LIVE-DIAG]
      toast({
        title: 'Richiesta inviata',
        description: 'La Voce Pro vedrà la tua richiesta in tempo reale.',
      });
      setOpen(false);
      setMessage('');
      onChanged?.();
    } catch (e) {
      toast({
        title: 'Impossibile inviare la richiesta',
        description: e.response?.data?.detail?.split('|').pop() || 'Riprova fra poco.',
        variant: 'destructive',
      });
    } finally { setBusy(false); }
  };

  const cancel = async () => {
    setBusy(true);
    try {
      await cancelCohostRequest(live.id);
      toast({ title: 'Richiesta annullata' });
      onChanged?.();
    } catch (_) {
      toast({ title: 'Impossibile annullare', variant: 'destructive' });
    } finally { setBusy(false); }
  };

  if (status === 'accepted') {
    return (
      <div
        className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-3 py-2.5 text-center text-xs font-semibold text-emerald-300"
        data-testid="cohost-accepted-pill"
      >
        <Sparkles className="mr-1.5 inline h-3.5 w-3.5" />
        Richiesta approvata! Tocca «Sali sul palco» nel video per partecipare.
      </div>
    );
  }

  if (status === 'on_stage') {
    return (
      <div className="space-y-2 rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-3" data-testid="cohost-on-stage-badge">
        <div className="flex items-center justify-center gap-2 text-sm font-semibold text-emerald-300">
          <Mic2 className="h-4 w-4" /> Stai partecipando alla live
        </div>
        <button
          onClick={() => window.dispatchEvent(new CustomEvent('nymia:leave-stage', { detail: { liveId: live.id } }))}
          className="flex w-full items-center justify-center gap-2 rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs font-semibold text-red-300 transition-colors hover:bg-red-500/20"
          data-testid="leave-participation-btn"
        >
          <LogOut className="h-3.5 w-3.5" /> Lascia la partecipazione
        </button>
      </div>
    );
  }

  if (status === 'requested') {
    return (
      <div
        className="flex items-center justify-between gap-2 rounded-xl border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs gold-text"
        data-testid="cohost-pending-pill"
      >
        <span className="inline-flex items-center gap-1.5 font-semibold">
          <Clock className="h-3.5 w-3.5" /> Richiesta in attesa di approvazione…
        </span>
        <button
          onClick={cancel}
          disabled={busy}
          className="rounded-md px-2 py-1 text-[11px] font-semibold underline-offset-2 hover:underline disabled:opacity-50"
          data-testid="cohost-cancel-btn"
        >
          {busy ? '…' : 'Annulla'}
        </button>
      </div>
    );
  }

  if (status === 'declined' || status === 'rejected') {
    return (
      <div
        className="rounded-xl border border-border bg-secondary/40 px-3 py-2.5 text-center text-xs text-muted-foreground"
        data-testid="cohost-declined-pill"
      >
        La tua richiesta di partecipazione è stata rifiutata.
      </div>
    );
  }

  // default — viewer can request to join the live
  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="flex w-full items-center justify-center gap-2 rounded-xl bg-neutral-900 px-4 py-3 text-sm font-semibold text-foreground shadow-md ring-1 ring-amber-500/30 transition hover:bg-neutral-800 hover:ring-amber-500/60"
        data-testid="cohost-request-btn"
      >
        <UserPlus className="h-4 w-4 gold-text" /> <span className="gold-text">Partecipa alla live</span>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="cohost-request-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 gold-text">
              <UserPlus className="h-4 w-4" /> Partecipa alla live
            </DialogTitle>
            <DialogDescription className="text-xs">
              La Voce Pro vedrà la tua richiesta in diretta. Se la approva, potrai
              salire sul palco con fotocamera e microfono. Aggiungi un breve
              messaggio (facoltativo).
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2 py-2">
            <Textarea
              placeholder="Ciao! Ti seguo con interesse, vorrei intervenire…"
              value={message}
              onChange={(e) => setMessage(e.target.value.slice(0, 140))}
              rows={3}
              data-testid="cohost-message-input"
            />
            <p className="text-right text-[11px] text-muted-foreground">{message.length}/140</p>
          </div>

          <DialogFooter className="flex flex-row justify-end gap-2">
            <Button variant="ghost" onClick={() => setOpen(false)} disabled={busy}>
              Annulla
            </Button>
            <Button
              onClick={sendRequest}
              disabled={busy}
              className="gold-gradient text-black"
              data-testid="cohost-request-submit"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Check className="mr-1.5 h-4 w-4" /> Invia richiesta</>)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CohostRequestButton;
